import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import EnquiryModal from "./EnquiryModal";
import {
  Phone, ChevronDown, ChevronUp, Menu, X, ArrowRight,
  Monitor, PenTool, Server, ShieldCheck, ArrowLeftRight,
  ScanSearch, Gauge, Wrench, LayoutTemplate,
  MailCheck, MapPin, ChartNoAxesCombined, FilePenLine, BotMessageSquare,
  Building2, BadgeCheck, ListChecks, Layers, CircleHelp,
} from "lucide-react";
import Logo from "./Logo";

/* ─── Services Mega Menu Data ─── */
const servicesCategories = [
  {
    title: "Design & Build",
    items: [
      { name: "Website Design", href: "/website-design", icon: Monitor, desc: "Bespoke websites built to grow your business." },
      { name: "Website Redesign", href: "/website-redesign", icon: PenTool, desc: "Give your existing website a modern refresh." },
      { name: "Landing Pages", href: "/landing-pages", icon: LayoutTemplate, desc: "Focused landing pages built to turn visitors into enquiries." },
    ],
  },
  {
    title: "Hosting & Management",
    items: [
      { name: "Managed Hosting", href: "/managed-hosting", icon: Server, desc: "Fast, secure hosting with expert UK support." },
      { name: "Website Transfers", href: "/website-transfers", icon: ArrowLeftRight, desc: "Seamless migrations with minimal downtime." },
      { name: "Website Care Plans", href: "/website-care-plans", icon: ShieldCheck, desc: "Updates, security and website maintenance." },
    ],
  },
  {
    title: "Support & Performance",
    items: [
      { name: "Security Audits", href: "/security-audits", icon: ScanSearch, desc: "Detailed security checks with clear reporting." },
      { name: "Website Optimisation", href: "/website-optimisation", icon: Gauge, desc: "Improve website speed, performance and usability." },
      { name: "One-Off Support", href: "/one-off-website-support", icon: Wrench, desc: "Flexible help with website changes and issues." },
    ],
  },
];

/* ─── Business Services Mega Menu Data ─── */
const businessCategories = [
  {
    title: "Setup & Communication",
    items: [
      { name: "Business Email Setup", href: "/business-email-setup", icon: MailCheck, desc: "Professional Microsoft 365 email setup and support." },
      { name: "Google Business Profile", href: "/google-business-profile", icon: MapPin, desc: "Professional Google profile setup and support." },
    ],
  },
  {
    title: "Growth & Insights",
    items: [
      { name: "Website Reporting", href: "/website-reporting", icon: ChartNoAxesCombined, desc: "Clear insights into traffic and website performance." },
      { name: "Content & Blog Writing", href: "/content-writing", icon: FilePenLine, desc: "Researched website content written for your business." },
    ],
  },
  {
    title: "Automation",
    items: [
      { name: "Website Assistants", href: "/website-assistants", icon: BotMessageSquare, desc: "Automated and AI-powered assistants for your website." },
    ],
  },
];

/* ─── About Mega Menu Data ─── */
const aboutCategories = [
  {
    title: "About CtrlAltComplete",
    items: [
      { name: "About Us", href: "/about-us", icon: Building2, desc: "Learn more about CtrlAltComplete and what we do." },
      { name: "Why Choose Us", href: "/#Why-Choose-Us", icon: BadgeCheck, desc: "Straightforward web services without the complication." },
    ],
  },
  {
    title: "Working with Us",
    items: [
      { name: "How It Works", href: "/#How-It-Works", icon: ListChecks, desc: "Simple from start to finish — our clear process." },
      { name: "Our Services", href: "/#Our-Services", icon: Layers, desc: "Everything your business needs to grow online." },
    ],
  },
  {
    title: "Help & Feedback",
    items: [
      { name: "FAQs", href: "/faq", icon: CircleHelp, desc: "Quick answers to common questions about our services." },
    ],
  },
];

/* ─── Reusable mega-menu item link ─── */
const MegaItem = ({ item, onClick }) => (
  <Link
    to={item.href}
    data-testid={`mega-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
    className="group flex items-start gap-4 p-3.5 rounded-lg hover:bg-primary/5 transition-colors"
    onClick={onClick}
  >
    <div className="w-[46px] h-[46px] bg-primary/15 flex items-center justify-center flex-shrink-0 rounded-md group-hover:bg-primary transition-colors">
      <item.icon className="w-[22px] h-[22px] text-primary group-hover:text-white transition-colors" strokeWidth={1.75} />
    </div>
    <div className="min-w-0 pt-0.5">
      <p className="text-[14.5px] font-extrabold text-foreground group-hover:text-primary transition-colors leading-tight">{item.name}</p>
      <p className="text-[12.5px] text-muted-foreground mt-0.5 leading-snug">{item.desc}</p>
    </div>
  </Link>
);

/* ─── Mobile accordion item ─── */
const MobileItem = ({ item, onClose }) => (
  <Link
    to={item.href}
    className="flex items-start gap-3 py-3 px-2 hover:bg-secondary/50 transition-colors rounded"
    onClick={onClose}
  >
    <div className="w-8 h-8 bg-primary/10 flex items-center justify-center flex-shrink-0 rounded">
      <item.icon className="w-4 h-4 text-primary" />
    </div>
    <div>
      <p className="text-sm font-bold text-foreground">{item.name}</p>
      <p className="text-xs text-muted-foreground">{item.desc}</p>
    </div>
  </Link>
);

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileBusinessOpen, setMobileBusinessOpen] = useState(false);
  const [mobileAboutOpen, setMobileAboutOpen] = useState(false);

  const [servicesOpen, setServicesOpen] = useState(false);
  const [businessOpen, setBusinessOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);

  const servicesTimer = useRef(null);
  const businessTimer = useRef(null);
  const aboutTimer = useRef(null);
  const headerRef = useRef(null);
  const location = useLocation();

  const [showEnquiry, setShowEnquiry] = useState(false);

  useEffect(() => {
    const handler = () => setShowEnquiry(true);
    window.addEventListener('openEnquiry', handler);
    return () => window.removeEventListener('openEnquiry', handler);
  }, []);

  /* Close all menus on route change */
  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileServicesOpen(false);
    setMobileBusinessOpen(false);
    setMobileAboutOpen(false);
    setServicesOpen(false);
    setBusinessOpen(false);
    setAboutOpen(false);
  }, [location]);

  /* Scroll listener */
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  /* Keep --header-h in sync via ResizeObserver (fixes mega menu gap) */
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      document.documentElement.style.setProperty("--header-h", `${el.offsetHeight}px`);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  /* Poll header height during scroll-state transition (py-4 ↔ py-3, 300ms) */
  useEffect(() => {
    let active = true;
    const sync = () => {
      if (headerRef.current) {
        document.documentElement.style.setProperty("--header-h", `${headerRef.current.offsetHeight}px`);
      }
      if (active) requestAnimationFrame(sync);
    };
    sync();
    const stop = setTimeout(() => { active = false; }, 400);
    return () => { active = false; clearTimeout(stop); };
  }, [isScrolled]);

  /* ─── Desktop mega menu hover handlers ─── */
  const openServices = () => {
    clearTimeout(servicesTimer.current);
    clearTimeout(businessTimer.current);
    clearTimeout(aboutTimer.current);
    setServicesOpen(true);
    setBusinessOpen(false);
    setAboutOpen(false);
  };
  const closeServices = () => {
    servicesTimer.current = setTimeout(() => setServicesOpen(false), 150);
  };

  const openBusiness = () => {
    clearTimeout(servicesTimer.current);
    clearTimeout(businessTimer.current);
    clearTimeout(aboutTimer.current);
    setBusinessOpen(true);
    setServicesOpen(false);
    setAboutOpen(false);
  };
  const closeBusiness = () => {
    businessTimer.current = setTimeout(() => setBusinessOpen(false), 150);
  };

  const openAbout = () => {
    clearTimeout(servicesTimer.current);
    clearTimeout(businessTimer.current);
    clearTimeout(aboutTimer.current);
    setAboutOpen(true);
    setServicesOpen(false);
    setBusinessOpen(false);
  };
  const closeAbout = () => {
    aboutTimer.current = setTimeout(() => setAboutOpen(false), 150);
  };

  /* Shared mega panel class */
  const megaPanelClass = (isOpen) =>
    `mega-menu absolute left-0 right-0 top-full transition-all duration-200 ${
      isOpen ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-2 pointer-events-none"
    }`;

  const closeMobile = () => {
    setMobileMenuOpen(false);
    setMobileServicesOpen(false);
    setMobileBusinessOpen(false);
    setMobileAboutOpen(false);
  };

  const handleStartProject = () => {
    closeMobile();
    setShowEnquiry(true);
  };

  const anyMenuOpen = servicesOpen || businessOpen || aboutOpen;

  return (
    <>
    <header
      ref={headerRef}
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,backdrop-filter,box-shadow] duration-300 ${
        isScrolled ? "glass-header" : "bg-white"
      } ${
        isScrolled && !anyMenuOpen ? "py-3" : "pt-[24px] pb-[20px]"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" data-testid="logo" className="flex items-center gap-3">
          <Logo />
          <span className="font-extrabold text-xl tracking-tight text-foreground">CtrlAltComplete</span>
        </Link>

        {/* ═══ Desktop Navigation ═══ */}
        <nav className="hidden lg:flex items-center gap-7" data-testid="desktop-nav">

          {/* ── Services Mega Menu ── */}
          <div className="static" data-testid="services-mega-trigger" onMouseEnter={openServices} onMouseLeave={closeServices}>
            <button className="flex items-center gap-1 text-base font-semibold text-foreground hover:text-primary transition-colors">
              Web Services <ChevronDown className="w-4 h-4" />
            </button>
            <div className={megaPanelClass(servicesOpen)} style={{ zIndex: 49 }} onMouseEnter={openServices} onMouseLeave={closeServices}>
              <div className="bg-white border-b border-border shadow-lg">
                <div className="max-w-[1280px] mx-auto px-8 py-7 flex">
                  <div className="flex-[3] grid grid-cols-3 gap-x-6" style={{ gridAutoRows: "auto" }}>
                    {servicesCategories.map((cat) => (
                      <span key={cat.title} className="inline-block text-xs tracking-[0.15em] uppercase font-extrabold text-primary mb-1 ml-3.5">{cat.title}</span>
                    ))}
                    {Array.from({ length: Math.max(...servicesCategories.map(c => c.items.length)) }).flatMap((_, rowIdx) =>
                      servicesCategories.map((cat) =>
                        cat.items[rowIdx] ? <MegaItem key={cat.items[rowIdx].name} item={cat.items[rowIdx]} /> : <div key={`${cat.title}-empty-${rowIdx}`} />
                      )
                    )}
                  </div>
                  <div className="flex-1 bg-secondary/60 border border-border rounded-lg ml-8 p-7 flex flex-col justify-center">
                    <h4 className="text-base font-extrabold text-foreground mb-2">Need Some Advice?</h4>
                    <p className="text-sm text-muted-foreground leading-relaxed mb-5">
                      Not sure which service is right for your business? We'll help you find the best solution with honest, no-obligation advice.
                    </p>
                    <Link to="/#contact" data-testid="mega-services-cta" className="btn-primary text-xs font-bold px-4 py-2.5 inline-flex items-center justify-center gap-1.5">
                      Contact Us <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── Business Services Mega Menu ── */}
          <div className="static" data-testid="business-mega-trigger" onMouseEnter={openBusiness} onMouseLeave={closeBusiness}>
            <button className="flex items-center gap-1 text-base font-semibold text-foreground hover:text-primary transition-colors">
              Business Services <ChevronDown className="w-4 h-4" />
            </button>
            <div className={megaPanelClass(businessOpen)} style={{ zIndex: 49 }} onMouseEnter={openBusiness} onMouseLeave={closeBusiness}>
              <div className="bg-white border-b border-border shadow-lg">
                <div className="max-w-[1280px] mx-auto px-8 py-7 flex">
                  <div className="flex-[3] grid grid-cols-3 gap-x-6" style={{ gridAutoRows: "auto" }}>
                    {businessCategories.map((cat) => (
                      <span key={cat.title} className="inline-block text-xs tracking-[0.15em] uppercase font-extrabold text-primary mb-2 ml-3.5">{cat.title}</span>
                    ))}
                    {Array.from({ length: Math.max(...businessCategories.map(c => c.items.length)) }).flatMap((_, rowIdx) =>
                      businessCategories.map((cat) =>
                        cat.items[rowIdx] ? <MegaItem key={cat.items[rowIdx].name} item={cat.items[rowIdx]} /> : <div key={`${cat.title}-empty-${rowIdx}`} />
                      )
                    )}
                  </div>
                  <div className="flex-1 bg-secondary/60 border border-border rounded-lg ml-8 p-7 flex flex-col justify-center">
                    <h4 className="text-base font-extrabold text-foreground mb-2">Grow Your Business Online</h4>
                    <p className="text-sm text-muted-foreground leading-relaxed mb-5">
                      From professional email to smart automation — we'll help you set up the tools your business needs.
                    </p>
                    <Link to="/#contact" data-testid="mega-business-cta" className="btn-primary text-xs font-bold px-4 py-2.5 inline-flex items-center justify-center gap-1.5">
                      Contact Us <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── About Mega Menu ── */}
          <div className="static" data-testid="about-mega-trigger" onMouseEnter={openAbout} onMouseLeave={closeAbout}>
            <button className="flex items-center gap-1 text-base font-semibold text-foreground hover:text-primary transition-colors">
              About <ChevronDown className="w-4 h-4" />
            </button>
            <div className={megaPanelClass(aboutOpen)} style={{ zIndex: 49 }} onMouseEnter={openAbout} onMouseLeave={closeAbout}>
              <div className="bg-white border-b border-border shadow-lg">
                <div className="max-w-[1280px] mx-auto px-8 py-7 flex">
                  <div className="flex-[3] grid grid-cols-3 gap-x-6" style={{ gridAutoRows: "auto" }}>
                    {aboutCategories.map((cat) => (
                      <span key={cat.title} className="inline-block text-xs tracking-[0.15em] uppercase font-extrabold text-primary mb-2 ml-3.5">{cat.title}</span>
                    ))}
                    {Array.from({ length: Math.max(...aboutCategories.map(c => c.items.length)) }).flatMap((_, rowIdx) =>
                      aboutCategories.map((cat) =>
                        cat.items[rowIdx] ? <MegaItem key={cat.items[rowIdx].name} item={cat.items[rowIdx]} /> : <div key={`${cat.title}-empty-${rowIdx}`} />
                      )
                    )}
                  </div>
                  <div className="flex-1 bg-secondary/60 border border-border rounded-lg ml-8 p-7 flex flex-col justify-center">
                    <h4 className="text-base font-extrabold text-foreground mb-2">Ready to Work Together?</h4>
                    <p className="text-sm text-muted-foreground leading-relaxed mb-5">
                      Have a project in mind or need some advice? Tell us what you're looking for and we'll point you in the right direction.
                    </p>
                    <Link to="/#contact" data-testid="mega-about-cta" className="btn-primary text-xs font-bold px-4 py-2.5 inline-flex items-center justify-center gap-1.5">
                      Contact Us <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Simple nav links */}
          <Link to="/portfolio" className="nav-link-underline text-base font-semibold text-foreground hover:text-primary transition-colors">Portfolio</Link>
          <Link to="/blog" className="nav-link-underline text-base font-semibold text-foreground hover:text-primary transition-colors">Blog</Link>
        </nav>

        {/* CTA Buttons */}
        <div className="hidden lg:flex items-center gap-4">
          <a href="tel:01217943680" data-testid="header-call-btn" className="flex items-center gap-2 text-base font-bold text-primary hover:text-primary/80 transition-colors">
            <Phone className="w-5 h-5" /> 0121 794 3680
          </a>
          <button onClick={handleStartProject} data-testid="header-start-project-btn" className="btn-primary px-5 py-2.5 text-sm font-bold">
            Start A Project
          </button>
        </div>

        {/* Mobile Menu Button */}
        <button data-testid="mobile-menu-btn" className="lg:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* ═══ Mobile Menu ═══ */}
      {mobileMenuOpen && (
        <div data-testid="mobile-menu" className="lg:hidden absolute top-full left-0 right-0 bg-white border-b border-border py-6 px-6 animate-fade-in max-h-[calc(100dvh-var(--header-h))] overflow-y-auto">
          <nav className="flex flex-col gap-1">

            {/* Services Accordion */}
            <div className="border-b border-border pb-2 mb-2">
              <button
                onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                data-testid="mobile-services-dropdown"
                className="flex items-center justify-between w-full py-3 text-lg font-semibold text-foreground hover:text-primary transition-colors cursor-pointer"
              >
                Web Services
                {mobileServicesOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </button>
              {mobileServicesOpen && (
                <div className="pb-3 animate-fade-in">
                  {servicesCategories.map((cat) => (
                    <div key={cat.title} className="mb-2">
                      <span className="inline-block text-[10px] tracking-[0.18em] uppercase font-bold text-primary mb-1 ml-2 mt-2">{cat.title}</span>
                      {cat.items.map((item) => (
                        <MobileItem key={item.name} item={item} onClose={closeMobile} />
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Business Services Accordion */}
            <div className="border-b border-border pb-2 mb-2">
              <button
                onClick={() => setMobileBusinessOpen(!mobileBusinessOpen)}
                data-testid="mobile-business-dropdown"
                className="flex items-center justify-between w-full py-3 text-lg font-semibold text-foreground hover:text-primary transition-colors cursor-pointer"
              >
                Business Services
                {mobileBusinessOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </button>
              {mobileBusinessOpen && (
                <div className="pb-3 animate-fade-in">
                  {businessCategories.map((cat) => (
                    <div key={cat.title} className="mb-2">
                      <span className="inline-block text-[10px] tracking-[0.18em] uppercase font-bold text-primary mb-1 ml-2 mt-2">{cat.title}</span>
                      {cat.items.map((item) => (
                        <MobileItem key={item.name} item={item} onClose={closeMobile} />
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* About Accordion */}
            <div className="border-b border-border pb-2 mb-2">
              <button
                onClick={() => setMobileAboutOpen(!mobileAboutOpen)}
                data-testid="mobile-about-dropdown"
                className="flex items-center justify-between w-full py-3 text-lg font-semibold text-foreground hover:text-primary transition-colors cursor-pointer"
              >
                About
                {mobileAboutOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </button>
              {mobileAboutOpen && (
                <div className="pb-3 animate-fade-in">
                  {aboutCategories.map((cat) => (
                    <div key={cat.title} className="mb-2">
                      <span className="inline-block text-[10px] tracking-[0.18em] uppercase font-bold text-primary mb-1 ml-2 mt-2">{cat.title}</span>
                      {cat.items.map((item) => (
                        <MobileItem key={item.name} item={item} onClose={closeMobile} />
                      ))}
                    </div>
                  ))}
                  <Link
                    to="/#contact"
                    className="btn-primary text-sm font-bold px-5 py-2.5 mt-2 ml-2 inline-flex items-center gap-1.5"
                    onClick={closeMobile}
                  >
                    Contact Us <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              )}
            </div>

            <Link to="/portfolio" className="py-3 text-lg font-semibold text-foreground hover:text-primary transition-colors" onClick={closeMobile}>Portfolio</Link>
            <Link to="/blog" className="py-3 text-lg font-semibold text-foreground hover:text-primary transition-colors" onClick={closeMobile}>Blog</Link>

            <a href="tel:01217943680" className="flex items-center gap-2 text-lg font-bold text-primary mt-4">
              <Phone className="w-5 h-5" /> 0121 794 3680
            </a>
            <button onClick={handleStartProject} className="btn-primary px-5 py-3 text-center font-bold mt-2 w-full" data-testid="mobile-start-project-btn">
              Start A Project
            </button>
          </nav>
        </div>
      )}
    </header>
    <EnquiryModal isOpen={showEnquiry} onClose={() => setShowEnquiry(false)} />
    </>
  );
};

export default Header;
